// src/pages/ia-paca/metiers/prompt-engineer.tsx
//
// Page métier — Prompt Engineer en PACA.

import MetierPageTemplate, {
  type MetierPageConfig,
} from '../../../components/ia-paca/MetierPageTemplate';
import { FileText, Sparkles, Workflow, ShieldCheck, Activity, Brain } from 'lucide-react';

const CONFIG: MetierPageConfig = {
  slug: 'prompt-engineer',
  metaTitle: 'Recrutement Prompt Engineer en PACA — Marseille, Sophia | recruteurs.io',
  metaDescription:
    'Recruter un Prompt Engineer en PACA : profils LLM, eval, guardrails, prompt versioning. Cabinet de recrutement spécialisé qui sait distinguer un vrai prompt engineer d\'un utilisateur ChatGPT avancé. Salaire 40-90k€.',
  metaKeywords:
    'recrutement Prompt Engineer, Prompt Engineer Marseille, Prompt Engineer Sophia, salaire Prompt Engineer 2026, recruter Prompt Engineer PACA',
  heroLabel: 'Prompt Engineer',
  heroSubtitle:
    "Le métier le plus survendu de 2024. En 2026, il existe vraiment — mais il ne consiste pas à écrire des prompts dans un Notion. Un vrai Prompt Engineer construit un système de prompts versionnés, testé, monitoré, optimisé.",
  heroBadges: ['LLM eval', 'Prompt versioning', 'Guardrails', 'LangFuse'],
  jsonLdName: 'Prompt Engineer',
  jsonLdDescription:
    "Le Prompt Engineer conçoit, teste, version et optimise les prompts d'un système IA en production. Travaille étroitement avec AI Engineers et Product Managers.",
  jsonLdSkills:
    'Prompt design, LLM evaluation (ragas, deepeval, promptfoo), prompt versioning (LangFuse, Promptlayer, Helicone), guardrails (Guardrails AI, NeMo), A/B testing prompts, structured output (JSON schema, function calling), Python basique, lecture de logs',
  jsonLdMinSalary: 40000,
  jsonLdMedianSalary: 60000,
  jsonLdMaxSalary: 90000,
  jsonLdEducation:
    "Background varié : linguistique, ingénieur, sciences cognitives, ex-product, auto-formé. Le titre se prouve sur portfolio.",
  jsonLdExperience: 'Junior (0-2 ans) à Senior (5+ ans).',
  intro: {
    title: 'Prompt Engineer : un vrai métier en 2026, à condition de bien le cadrer.',
    paragraphs: [
      "Un Prompt Engineer ne se résume pas à \"savoir bien parler à ChatGPT\". Sur un système LLM en production, le prompt fait partie du <strong>code applicatif critique</strong>. Le faire évoluer sans casser les sorties demande une discipline : versioning, tests automatisés, métriques d'éval, A/B testing.",
      "Un Prompt Engineer mature passe ses journées à : itérer sur un prompt en local, lancer un eval framework (promptfoo, ragas, deepeval), comparer 2 versions sur un dataset de référence, monitorer LangFuse ou Helicone pour repérer les régressions, et collaborer avec les AI Engineers pour structurer les sorties (function calling, JSON schema, guardrails).",
      "<strong>Le piège du marché :</strong> 70% des candidats qui se disent Prompt Engineer sont en réalité des utilisateurs avancés de ChatGPT. Ils savent obtenir une bonne réponse en chat, mais ils ne savent pas industrialiser un prompt en prod. Notre job : faire la différence dès le brief.",
    ],
  },
  stackTitle: 'Les outils que votre Prompt Engineer doit maîtriser.',
  stack: [
    {
      Icon: Brain,
      title: 'LLM APIs',
      tools: ['OpenAI (GPT-4o, o1)', 'Anthropic (Claude)', 'Mistral', 'Google (Gemini)', 'Cohere'],
    },
    {
      Icon: FileText,
      title: 'Prompt versioning',
      tools: ['LangFuse', 'Promptlayer', 'Helicone', 'BAML', 'Git pour prompts'],
    },
    {
      Icon: Activity,
      title: 'Eval frameworks',
      tools: ['promptfoo', 'ragas', 'deepeval', 'OpenAI Evals', 'TruLens'],
    },
    {
      Icon: ShieldCheck,
      title: 'Guardrails & structure',
      tools: ['Guardrails AI', 'NVIDIA NeMo Guardrails', 'JSON schema', 'function calling', 'Pydantic'],
    },
    {
      Icon: Workflow,
      title: 'A/B testing & rollout',
      tools: ['LaunchDarkly', 'GrowthBook', 'Eppo', 'feature flags', 'canary deployment'],
    },
    {
      Icon: Sparkles,
      title: 'Outils pratiques',
      tools: ['Jupyter / VS Code', 'Python basique', 'Cursor / Continue', 'LangChain prompts', 'JSON viewers'],
    },
  ],
  salaryByCityTitle: '40-90 k€, salaire fortement lié au niveau de discipline industrielle.',
  salaryByCity: [
    { ville: 'Marseille', junior: '40 – 50 k€', confirme: '50 – 65 k€', senior: '65 – 85 k€', expert: '80 – 90 k€' },
    { ville: 'Aix-en-Provence', junior: '38 – 48 k€', confirme: '48 – 62 k€', senior: '62 – 82 k€', expert: '78 – 88 k€' },
    { ville: 'Sophia Antipolis', junior: '42 – 52 k€', confirme: '52 – 68 k€', senior: '68 – 88 k€', expert: '85 – 95 k€' },
    { ville: 'Nice', junior: '40 – 50 k€', confirme: '50 – 65 k€', senior: '65 – 85 k€', expert: '80 – 90 k€' },
  ],
  questionsTitle: '5 questions pour distinguer un vrai Prompt Engineer.',
  questionsIntro:
    "Ces questions séparent en 10 minutes un Prompt Engineer industriel d'un utilisateur avancé de ChatGPT.",
  questions: [
    {
      q: 'Décris ton process pour itérer sur un prompt en production.',
      good:
        "Cite : un dataset d'évaluation construit avec les experts métier, un eval framework (promptfoo / ragas / deepeval), prompt versionné en git ou LangFuse, A/B testing entre versions, métriques business (pas juste 'ça a l'air mieux'), rollout progressif.",
      bad: "'Je modifie le prompt et je teste avec quelques exemples.' Pas de dataset, pas de versioning, pas d'A/B. Approche bricolage.",
    },
    {
      q: "Tu as un prompt qui marche dans 95% des cas mais qui plante dans 5%. Comment tu débugges ?",
      good:
        'Analyse les 5% : sont-ce des inputs adversariaux, des cas limites métier, des ambiguïtés ? Ajoute des few-shot examples ciblés, structure la sortie (JSON schema + retry), ajoute une étape de validation post-LLM, ou route vers un modèle plus puissant sur ces cas. Mesure que le fix ne dégrade pas les 95% qui marchaient.',
      bad: "'J'ajoute une instruction dans le prompt pour mieux gérer ces cas.' Sans mesurer la régression sur le reste, ça peut casser plus que ça répare.",
    },
    {
      q: 'Pourquoi utiliserais-tu function calling / structured output plutôt que parser un texte libre ?',
      good:
        "Fiabilité : la sortie respecte un schéma, parsing déterministe, validation immédiate, possibilité de typer côté code. Économies (moins de tokens en sortie). Permet de chainer avec d'autres systèmes. Connaît Pydantic, JSON schema, et les limites (function calling peut être moins créatif que texte libre).",
      bad: "'C'est pareil, on peut parser le texte.' Ignore les pannes de format, les hallucinations de structure, les coûts.",
    },
    {
      q: "Comment tu gères les hallucinations dans un système qui doit citer des sources internes ?",
      good:
        "Combine plusieurs leviers : RAG correctement architecturé en amont (chunking + reranker), prompt système strict 'cite tes sources, refuse si non trouvé', éval de faithfulness sur dataset, guardrails post-LLM pour vérifier que les citations existent réellement dans le contexte. Pas de silver bullet.",
      bad: "'Je mets dans le prompt : ne hallucine pas.' Approche naïve, prouvée inefficace en prod.",
    },
    {
      q: "Tu as 3 modèles à choisir : GPT-4o, Claude Sonnet, Mistral Large. Comment tu décides pour un cas d'usage donné ?",
      good:
        "Réponse contextuelle : benchmarke sur un dataset propre au cas d'usage, compare qualité + latence + coût + structured output + capacité multilingue selon besoin. Tient compte de la souveraineté (Mistral pour clients sensibles). Sait que le 'meilleur modèle' dépend du cas.",
      bad: "'GPT-4o c'est le meilleur.' Réponse dogmatique. Pas de notion benchmark, pas de notion coût, pas de pratique terrain.",
    },
  ],
  ctaTitle: 'Vous cherchez un Prompt Engineer en PACA ?',
  ctaSubtitle:
    "Profil souvent confondu avec un utilisateur de ChatGPT. Notre court-list filtre sur la pratique industrielle. 4-6 candidats qualifiés en 15-20 jours.",
};

export default function PromptEngineerPage() {
  return <MetierPageTemplate config={CONFIG} />;
}
